import {
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
} from "@coreui/react";
import {
  ElectronicDocumentStatusHistory,
  ChangeEntry,
  describeActor,
} from "../../models/electronic-document-status-history";
import { formatPygDateTime } from "../../utils/dates";
import { DOCUMENT_TYPE_CHOICES } from "../../models/documents";
import ElectronicDocumentStatusBadge from "./ElectronicDocumentStatusBadge";

interface ElectronicDocumentHistoryDetailModalProps {
  show: boolean;
  record?: ElectronicDocumentStatusHistory;
  onCancel: () => void;
}

const ElectronicDocumentHistoryDetailModal: React.FC<
  ElectronicDocumentHistoryDetailModalProps
> = ({ show, record, onCancel }) => {
  const renderValue = (value: any): string => {
    if (value === null || value === undefined) {
      return "-";
    }
    if (typeof value === "object") {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const formatFieldName = (name: string): string => {
    // Convert camelCase to readable format
    return name
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  return (
    <CModal show={show} onClose={onCancel} size="xl">
      <CModalHeader closeButton>
        <CModalTitle>Detalle de Cambios</CModalTitle>
      </CModalHeader>
      <CModalBody>
        {record && (
          <>
            <CCard className="mb-3">
              <CCardHeader>
                <strong>Información del Registro</strong>
              </CCardHeader>
              <CCardBody>
                <CRow>
                  <CCol md="6">
                    <p>
                      <strong>Fecha:</strong>{" "}
                      {record.createdAt
                        ? formatPygDateTime(new Date(record.createdAt))
                        : "-"}
                    </p>
                    <p>
                      <strong>Estado:</strong>{" "}
                      <ElectronicDocumentStatusBadge status={record.status} />
                    </p>
                    <p>
                      <strong>Autor:</strong> {describeActor(record)}
                      {record.actor && (
                        <span className="text-muted">
                          {" "}
                          ({record.actor.email})
                        </span>
                      )}
                    </p>
                    <p>
                      <strong>Tipo de Documento:</strong>{" "}
                      {record.documentType
                        ? DOCUMENT_TYPE_CHOICES.get(record.documentType) ||
                          record.documentType
                        : "-"}
                    </p>
                  </CCol>
                  <CCol md="6">
                    <p>
                      <strong>CDC:</strong> {record.cdc || "-"}
                    </p>
                    <p>
                      <strong>Código de Respuesta:</strong>{" "}
                      {record.setResponseCode || "-"}
                    </p>
                    <p>
                      <strong>Descripción de Respuesta:</strong>{" "}
                      {record.setResponseDescription || "-"}
                    </p>
                  </CCol>
                </CRow>
                {record.setError && (
                  <CRow>
                    <CCol md="12">
                      <p>
                        <strong>Error:</strong>
                      </p>
                      <pre
                        style={{
                          backgroundColor: "var(--theme-surface-raised)",
                          padding: "10px",
                          borderRadius: "4px",
                          maxHeight: "200px",
                          overflow: "auto",
                        }}
                      >
                        {record.setError}
                      </pre>
                    </CCol>
                  </CRow>
                )}
              </CCardBody>
            </CCard>

            {record.changes && record.changes.length > 0 && (
              <CCard>
                <CCardHeader>
                  <strong>Campos Modificados</strong>
                </CCardHeader>
                <CCardBody>
                  {record.changes.map((change: ChangeEntry, index: number) => (
                    <div
                      key={index}
                      style={{
                        marginBottom: "20px",
                        padding: "15px",
                        backgroundColor: "var(--theme-surface-raised)",
                        borderRadius: "4px",
                        border: "1px solid var(--theme-border)",
                      }}
                    >
                      <h6 style={{ marginBottom: "10px", color: "var(--theme-text-soft)" }}>
                        <strong>{formatFieldName(change.name)}</strong>
                      </h6>
                      <CRow>
                        <CCol md="6">
                          <p style={{ marginBottom: "5px" }}>
                            <strong>Valor Anterior:</strong>
                          </p>
                          <pre
                            style={{
                              backgroundColor: "var(--theme-surface)",
                              padding: "10px",
                              borderRadius: "4px",
                              border: "1px solid var(--theme-border)",
                              maxHeight: "150px",
                              overflow: "auto",
                              marginBottom: "0",
                            }}
                          >
                            {renderValue(change.changes.old)}
                          </pre>
                        </CCol>
                        <CCol md="6">
                          <p style={{ marginBottom: "5px" }}>
                            <strong>Valor Nuevo:</strong>
                          </p>
                          <pre
                            style={{
                              backgroundColor: "var(--theme-surface)",
                              padding: "10px",
                              borderRadius: "4px",
                              border: "1px solid var(--theme-border)",
                              maxHeight: "150px",
                              overflow: "auto",
                              marginBottom: "0",
                            }}
                          >
                            {renderValue(change.changes.new)}
                          </pre>
                        </CCol>
                      </CRow>
                    </div>
                  ))}
                </CCardBody>
              </CCard>
            )}

            {(!record.changes || record.changes.length === 0) && (
              <CCard>
                <CCardBody>
                  <p className="text-center text-muted">
                    No hay cambios registrados para este estado.
                  </p>
                </CCardBody>
              </CCard>
            )}
          </>
        )}
      </CModalBody>
    </CModal>
  );
};

export default ElectronicDocumentHistoryDetailModal;
